import api from 'api';
import { createSlice } from '@reduxjs/toolkit';
import { Dispatch } from 'redux';
import { IVehicle } from './vehicleSlice';
import { IClientUser } from './userSlice';
import { IDefaultOption } from 'components/common/select';
import { convertDropdownList } from 'helpers/utils';
import { IUploadImageRequestParam } from './organizationSlice';
import axios from 'axios';
import {
  IWorkshopAppointmentListItem,
  IWorkshopAppointmentVehicleOption,
} from 'interfaces/workshop-appointment-interface';
import { ICompanyDetails } from 'interfaces/company-interface';

interface IWorkshopAppointmentVehicleData
  extends Omit<
    IVehicle,
    'organization_name' | 'company_name' | 'assigned_user_name' | 'vehicle_image_url'
  > {
  organization_id: string;
  vehicle_img: string;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  associated_company: string;
  brand_id: string;
  chasis: string;
  model: string;
  past_user_ids: string[];
  plate: string;
  purchase_date: string;
  registration_date: string;
  status: string;
  user_id: string;
  _id: string;
}
interface IWorkshopAppointmentVehicleDetails {
  _id: string;
  associated_company: string;
  category: string;
  chasis: string;
  doors: string;
  engine_type: string;
  environmental_label: boolean;
  frame: string;
  fuel_type: string;
  is_used: boolean;
  km: string;
  places: string;
  plate: string;
  tire_size: string;
  transmission: string;
  tuition: string;
  vehicle_id: string;
  version: string;
  parking_id: string;
  created_at: string;
  engine_id: string;
  tire_size_id: string;
  updated_at: string;
}
export interface IWorkshopAppointmentCustomerData extends Omit<IClientUser, 'date_of_birth'> {
  password: string;
  birth_date: string;
  is_deleted: boolean;
  email_verify: boolean;
  device_token: string;
  created_at: string;
  updated_at: string;
  __v: 0;
  company_id: string;
}
export interface IMedia {
  _id: string;
  url: string;
  type: string;
  key: string;
  is_deleted: boolean;
}
export interface IWorkshopAppointment {
  admin_media: IMedia[];
  alert_details: {
    alert_ref: string;
    created_at: string;
    description: string;
    is_deleted: boolean;
    is_read: boolean;
    notes?: string;
    status: string;
    title: string;
    type: string;
    updated_at: string;
    _id: string;
  };
  alert_id: string;
  brand_data: {
    asset_id: string;
    created_at: string;
    is_deleted: boolean;
    name: string;
    updated_at: string;
    _id: string;
  };
  company_data: ICompanyDetails;
  model_data: {
    asset_id: string;
    brand_id: string;
    created_at: string;
    engine_id: string;
    is_deleted: boolean;
    name: string;
    tire_size_id: string;
    updated_at: string;
    _id: string;
  };
  type: string;

  _id?: string;
  replace_car: boolean;
  service_description: string;
  service_type: {
    _id: string;
    service_type: string;
    __v: string;
  };
  service_type_id?: string;
  slot: {
    _id: string;
    organization_id: string;
    parking_id: string;
    appointment_id: string;
    date: string;
    time: string;
    time_division: string;
    is_booked: boolean;
    __v: string;
  };
  user_id: string;
  vehicle_id: string;
  status: string;
  is_deleted: boolean;
  slot_id: string;
  vehicle_details_data: IWorkshopAppointmentVehicleDetails;
  vehicle_data: IWorkshopAppointmentVehicleData;
  other_service_type?: string;
  special_note?: string;
  user_details: IWorkshopAppointmentCustomerData;
  created_at?: string;
  media?: IMedia[];
  updated_at?: string;
}

export interface IBookAvailableSlotRequest {
  organization_id: string;
  parking_id: string;
  appointment_id: string;
  slot_details: { time?: string; date?: string; time_division?: string }[];
}
export interface IWorkshopAppointmentServicetype extends IDefaultOption {
  _id: string;
  service_type: string;
}
export interface IAddEditWorkshopAppointmentRequest
  extends Pick<
    IWorkshopAppointment,
    'other_service_type' | 'service_description' | 'special_note' | 'vehicle_id' | 'user_id'
  > {
  _id?: string;
  media: string[];
  type?: string;
  organization_id: string;
  parking_id: string;
  time: string;
  date: string;
  time_division?: string;
  replace_car: boolean;
  alert_id?: string;
}

export interface IAddWorkshopAppointmentRequest
  extends Omit<
    IAddEditWorkshopAppointmentRequest,
    '_id' | 'organization_id' | 'parking_id' | 'time' | 'date' | 'time_division' | 'user_id'
  > {
  user_id?: string;
}

export interface IEditWorkshopAppointmentRequest extends IAddEditWorkshopAppointmentRequest {
  status?: string;
  slot_id?: string;
}
export interface IConfirmWorkshopAppointmentRequest {
  appointment_id: string;
  slot_id: string;
  status: string;
  reject_reason?: string;
}

interface IUserState {
  workshopAppointments: IWorkshopAppointmentListItem[];
  selectedVehicleAppointments: IWorkshopAppointmentListItem[];
  workshopAppointmentServiceType: IWorkshopAppointmentServicetype[];
  workshopAppointmentDetails: IWorkshopAppointmentListItem | null;
  vehicles: IWorkshopAppointmentVehicleOption[];
  totalCount: number;
}
export interface IChangeWorkshopAppointmentStatusRequest {
  appointment_id: string;
  status: string;
  reject_reason?: string;
  admin_media?: string[];
  admin_special_note?: string;
}

interface IWorkshopAppointmentResponse {
  workshopDetail: IWorkshopAppointmentListItem[];
  record_count: number;
}
const initialState: IUserState = {
  workshopAppointments: [],
  selectedVehicleAppointments: [],
  workshopAppointmentServiceType: [],
  workshopAppointmentDetails: null,
  vehicles: [],
  totalCount: 0,
};
const userSlice = createSlice({
  name: 'User',
  initialState: initialState,
  reducers: {
    setWorkshopAppointmentList: (state, { payload }: { payload: IWorkshopAppointmentResponse }) => {
      state.workshopAppointments = [...payload.workshopDetail];
      state.totalCount = payload.record_count;
    },
    setSelectedVehicleAppointmentList: (
      state,
      { payload }: { payload: IWorkshopAppointmentResponse },
    ) => {
      state.selectedVehicleAppointments = [...payload.workshopDetail];
      state.totalCount = payload.record_count;
    },
    setworkshopAppointmentServiceTypeList: (
      state,
      { payload }: { payload: IWorkshopAppointmentServicetype[] },
    ) => {
      state.workshopAppointmentServiceType = [...payload];
    },
    setWorkshopAppointmentDetails: (
      state,
      { payload }: { payload: IWorkshopAppointmentListItem | null },
    ) => {
      state.workshopAppointmentDetails = payload;
    },
    setVehicles: (state, { payload }: { payload: IWorkshopAppointmentVehicleOption[] }) => {
      state.vehicles = payload;
    },
  },
});

export const getWorkshopAppointmentList =
  (
    payload: {
      params: { status: string[] };
      searchString?: string;
      brands?: string[];
      offset?: number;
      limit?: number;
    },
    signal?: AbortSignal,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const { params, brands, searchString, offset, limit } = payload;
      const statusQueryString = params.status
        .map(status => `status=${encodeURIComponent(status)}`)
        .join('&');
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}${
        searchString ? `&searchString=${encodeURIComponent(searchString)}` : ''
      }&${statusQueryString}`;

      const { data: responseData }: { data: { data: any } } = await api.post(
        `/admin/workshop${queryString}`,
        {
          brands: brands,
          // Add any other parameters you want to send in the body here
        },
        { signal },
      );

      dispatch(setWorkshopAppointmentList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getVehicleAppointmentList =
  (
    payload: { 
      vehicle_id: string,
      offset?: number;
      limit?: number;
    }
  ) => async (dispatch: Dispatch) => {
    try {
      const { vehicle_id, offset, limit } = payload;
      const queryString = `?offset=${offset ?? 0}&limit=${limit ?? 20}`;
      
      const { data: responseData }: { data: { data: IWorkshopAppointmentResponse } } =
        await api.get(`/admin/workshop/${vehicle_id}${queryString}`);
      dispatch(setSelectedVehicleAppointmentList(responseData?.data));
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getWorkshopAppointmentServicetypes = () => async (dispatch: Dispatch) => {
  try {
    const { data: responseData }: { data: { data: { _id: string; service_type: string }[] } } =
      await api.get('/admin/service');
    const updatedData = convertDropdownList(responseData?.data, 'service_type', 'name');
    dispatch(
      setworkshopAppointmentServiceTypeList([
        ...updatedData,
        { _id: 'OTHER', id: 'OTHER', name: 'Other', service_type: 'Other' },
      ]),
    );
    return Promise.resolve(responseData?.data);
  } catch (error) {
    return Promise.reject(error);
  }
};
export const createWorkshopAppointment =
  (payload: { request: IAddEditWorkshopAppointmentRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData }: { data: { data: IWorkshopAppointment[] } } = await api.post(
        '/admin/appointment',
        request,
      );
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const editWorkshopAppointment =
  (payload: { request: IEditWorkshopAppointmentRequest; appointment_id: string }) =>
  async (dispatch: Dispatch) => {
    try {
      const { request, appointment_id } = payload;
      const { data: responseData }: { data: { data: IWorkshopAppointment[] } } = await api.post(
        `/admin/appointment/${appointment_id}`,
        request,
      );
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const uploadWorkshopMedia =
  (payload: { params: IUploadImageRequestParam }) => async (dispatch: Dispatch) => {
    try {
      const {
        params: { request, file },
      } = payload;
      const constfile = file;
      const { data: responseData } = await api.get('/admin/assets/get/workshop', {
        params: { ...request },
      });
      await axios.put(`${responseData?.data?.url}`, constfile);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const changeWorkshopAppointmentStatus =
  (payload: { request: IChangeWorkshopAppointmentStatusRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/change/appointment/status', request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const bookAvailableSlot =
  (payload: { request: IBookAvailableSlotRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post('/admin/slot', request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const requestForWorkshopAppointment =
  (payload: { request: IAddWorkshopAppointmentRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData }: { data: { data: IWorkshopAppointment[] } } = await api.post(
        `/admin/fleet/appointment`,
        request,
      );
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const getWorkshopAppointmentAvailableSlots =
  (payload: { appointment_id: string }) => async (dispatch: Dispatch) => {
    try {
      const { appointment_id } = payload;
      const { data: responseData } = await api.get(`/admin/fleet/slots/${appointment_id}`);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const confirmWorkshopAppointment =
  (payload: { request: IConfirmWorkshopAppointmentRequest }) => async (dispatch: Dispatch) => {
    try {
      const { request } = payload;
      const { data: responseData } = await api.post(`/admin/fleet/confirm/slot`, request);
      return Promise.resolve(responseData?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const getVehiclesOptions =
  (payload: {
    params?: { company_id?: string[]; organization_id?: string; is_from_book_workshop?: boolean };
  }) =>
  async (dispatch: Dispatch) => {
    try {
      const { params } = payload;
      const { data: responseData } = await api.get('/admin/vehicle/getVehicleNames', { params });
      dispatch(setVehicles(responseData?.data?.vehicleList));
      return responseData?.data?.vehicleList;
    } catch (error) {
      return Promise.reject(error);
    }
  };
export const {
  setWorkshopAppointmentList,
  setSelectedVehicleAppointmentList,
  setworkshopAppointmentServiceTypeList,
  setWorkshopAppointmentDetails,
  setVehicles,
} = userSlice.actions;
export default userSlice.reducer;
